/**
 * 仕様書 §6「サーバー側の責務」の最低限バリデーション。
 *
 * サーバー（DO）はここにある `validateEntity` だけを実行する:
 *   - type が既知の種類か
 *   - 数値フィールドが NaN / Infinity / 極端に大きい座標でないか
 *   - plane の points が3点以上あるか
 *
 * 平面判定（§4.3）のような「ジオメトリの正当性チェック」はクライアント側の
 * 責務なので、`isPlanar` として分離してある（サーバーからは呼ばない）。
 */

import type {
  BraceArc,
  BraceGeometry,
  BracePathHole,
  ClothBake,
  ClothMesh,
  ClothPin,
  ClothShape,
  ClothSimSettings,
  Entity,
  EntityComponents,
  ObjectGeometry,
  ObjectMountAxis,
  ObjectYaw,
  PointLightComponent,
  StairGeometry,
  Vec3,
} from './types';
import { isObjectMountAxis, isObjectYaw } from './object-pose';

/** ワールドの座標上限（±）。これを超える値は異常値として弾く。 */
export const WORLD_LIMIT = 4096;
/** 断面サイズ・厚みの上限 */
export const MAX_THICKNESS = 256;
/** プレーンの頂点数上限（1メッセージあたりの負荷を抑えるため） */
export const MAX_PLANE_POINTS = 256;
/** ブレース折れ線の頂点数上限。プレーンと同じ。 */
export const MAX_BRACE_POINTS = MAX_PLANE_POINTS;
/** material 文字列の長さ上限 */
export const MAX_MATERIAL_LENGTH = 64;
/** 平面判定の許容誤差 */
export const PLANARITY_EPSILON = 1e-3;
/** 点光源コンポーネントの既定・上限値 */
export const DEFAULT_LIGHT_INTENSITY = 2;
export const MAX_LIGHT_INTENSITY = 16;
export const DEFAULT_LIGHT_RANGE = 12;
export const MAX_LIGHT_RANGE = 64;
export const DEFAULT_LIGHT_COLOR = 0xfff1c8;
/** 1 Entity に付けられる点光源数。モデル由来の異常な負荷を防ぐ。 */
export const MAX_ENTITY_LIGHTS = 8;
/** 1 立体あたりの穴の数上限。 */
export const MAX_HOLES = 32;

/** definitionId の長さ上限 */
export const MAX_OBJECT_DEFINITION_ID = 64;

const ENTITY_INPUT_TYPES = ['brace', 'plane', 'stair', 'object'] as const;
type EntityInputType = (typeof ENTITY_INPUT_TYPES)[number];

export type ValidationResult<T> = { ok: true; value: T } | { ok: false; error: string };

function fail(error: string): { ok: false; error: string } {
  return { ok: false, error };
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function validateVec3(v: unknown, path: string): ValidationResult<Vec3> {
  if (!isRecord(v)) return fail(`${path} must be an object with x/y/z`);
  for (const axis of ['x', 'y', 'z'] as const) {
    const n = v[axis];
    if (!isFiniteNumber(n)) return fail(`${path}.${axis} must be a finite number`);
    if (Math.abs(n) > WORLD_LIMIT) return fail(`${path}.${axis} exceeds world limit (±${WORLD_LIMIT})`);
  }
  return { ok: true, value: { x: v.x as number, y: v.y as number, z: v.z as number } };
}

function validatePositiveSize(v: unknown, path: string, max: number): ValidationResult<number> {
  if (!isFiniteNumber(v)) return fail(`${path} must be a finite number`);
  if (v <= 0) return fail(`${path} must be greater than 0`);
  if (v > max) return fail(`${path} must be <= ${max}`);
  return { ok: true, value: v };
}

/** プレーンの厚み。0 は厚みなしの面として許可する。 */
function validateNonNegativeSize(v: unknown, path: string, max: number): ValidationResult<number> {
  if (!isFiniteNumber(v)) return fail(`${path} must be a finite number`);
  if (v < 0) return fail(`${path} must be >= 0`);
  if (v > max) return fail(`${path} must be <= ${max}`);
  return { ok: true, value: v };
}

/** 押し出し距離。符号付き。0 は省略する。 */
function validateSignedSize(v: unknown, path: string, max: number): ValidationResult<number> {
  if (!isFiniteNumber(v)) return fail(`${path} must be a finite number`);
  if (Math.abs(v) > max) return fail(`${path} must be between ${-max} and ${max}`);
  return { ok: true, value: v };
}

function vecEqual(a: Vec3, b: Vec3): boolean {
  return a.x === b.x && a.y === b.y && a.z === b.z;
}

function validateBraceArcs(
  input: unknown,
  pointCount: number,
  closed: boolean
): ValidationResult<BraceArc[] | undefined> {
  if (input === undefined) return { ok: true, value: undefined };
  if (!Array.isArray(input)) return fail('geometry.arcs must be an array');
  const count = closed ? pointCount : pointCount - 1;
  const seen = new Set<number>();
  const arcs: BraceArc[] = [];
  for (let i = 0; i < input.length; i++) {
    const raw = input[i];
    if (!isRecord(raw)) return fail(`geometry.arcs[${i}] must be an object`);
    const segment = raw.segment;
    if (!Number.isInteger(segment) || (segment as number) < 0 || (segment as number) >= count) {
      return fail(`geometry.arcs[${i}].segment must be an integer 0–${Math.max(0, count - 1)}`);
    }
    if (seen.has(segment as number)) return fail('geometry.arcs must not repeat a segment');
    seen.add(segment as number);
    const center = validateVec3(raw.center, `geometry.arcs[${i}].center`);
    if (!center.ok) return center;
    arcs.push({ segment: segment as number, center: center.value });
  }
  if (arcs.length === 0) return { ok: true, value: undefined };
  arcs.sort((a, b) => a.segment - b.segment);
  return { ok: true, value: arcs };
}

function parsePathThickness(input: unknown): ValidationResult<number> {
  if (typeof input === 'number') return validatePositiveSize(input, 'geometry.thickness', MAX_THICKNESS);
  if (!isRecord(input)) return fail('geometry.thickness must be a number');
  const width = validatePositiveSize(input.width, 'geometry.thickness', MAX_THICKNESS);
  if (!width.ok) return width;
  if (input.height !== undefined) {
    const height = validatePositiveSize(input.height, 'geometry.thickness', MAX_THICKNESS);
    if (!height.ok) return height;
  }
  return width;
}

function validateBraceGeometry(input: Record<string, unknown>): ValidationResult<BraceGeometry> {
  const thickness = parsePathThickness(input.thickness);
  if (!thickness.ok) return thickness;

  let rotation = 0;
  if (input.rotation !== undefined) {
    if (!isFiniteNumber(input.rotation)) return fail('geometry.rotation must be a finite number');
    rotation = input.rotation;
  }

  let closed = false;
  if (input.closed !== undefined) {
    if (typeof input.closed !== 'boolean') return fail('geometry.closed must be a boolean');
    closed = input.closed;
  }

  if (input.points !== undefined) {
    const rawPoints = input.points;
    if (!Array.isArray(rawPoints)) return fail('geometry.points must be an array');
    const minPoints = closed ? 3 : 2;
    if (rawPoints.length < minPoints) {
      return fail(
        closed
          ? 'geometry.points must contain at least 3 points when closed'
          : 'geometry.points must contain at least 2 points'
      );
    }
    if (rawPoints.length > MAX_BRACE_POINTS) {
      return fail(`geometry.points must contain at most ${MAX_BRACE_POINTS} points`);
    }
    const points: Vec3[] = [];
    for (let i = 0; i < rawPoints.length; i++) {
      const parsed = validateVec3(rawPoints[i], `geometry.points[${i}]`);
      if (!parsed.ok) return parsed;
      points.push(parsed.value);
    }
    const count = closed ? points.length : points.length - 1;
    for (let i = 0; i < count; i++) {
      const a = points[i];
      const b = points[(i + 1) % points.length];
      if (vecEqual(a, b)) return fail('geometry.points must not contain identical adjacent vertices');
    }
    const arcs = validateBraceArcs(input.arcs, points.length, closed);
    if (!arcs.ok) return arcs;
    const result: BraceGeometry = {
      start: points[0],
      end: points[points.length - 1],
      points,
      thickness: thickness.value,
      rotation,
    };
    if (closed) result.closed = true;
    if (arcs.value) result.arcs = arcs.value;
    return attachBraceExtrude(result, input);
  }

  const start = validateVec3(input.start, 'geometry.start');
  if (!start.ok) return start;
  const end = validateVec3(input.end, 'geometry.end');
  if (!end.ok) return end;
  if (vecEqual(start.value, end.value)) {
    return fail('geometry.start and geometry.end must not be identical');
  }
  if (closed) return fail('geometry.points must contain at least 3 points when closed');
  const arcs = validateBraceArcs(input.arcs, 2, false);
  if (!arcs.ok) return arcs;
  const result: BraceGeometry = {
    start: start.value,
    end: end.value,
    thickness: thickness.value,
    rotation,
  };
  if (arcs.value) result.arcs = arcs.value;
  return attachBraceExtrude(result, input);
}

function isFillBraceInput(geometry: Record<string, unknown>): boolean {
  if (geometry.fill === true || geometry.mode === 'fill') return true;
  if (geometry.fill === false || geometry.mode === 'path') return false;
  if (typeof geometry.slab === 'number') return true;
  return (
    typeof geometry.thickness === 'number' &&
    geometry.start === undefined &&
    geometry.end === undefined &&
    Array.isArray(geometry.points) &&
    geometry.points.length >= 3
  );
}

function parseFillThickness(input: Record<string, unknown>): ValidationResult<number> {
  if (input.slab !== undefined) return validateNonNegativeSize(input.slab, 'geometry.slab', MAX_THICKNESS);
  if (typeof input.thickness === 'number' || input.thickness === undefined) {
    return validateNonNegativeSize(input.thickness ?? 0, 'geometry.thickness', MAX_THICKNESS);
  }
  if (!isRecord(input.thickness)) return fail('geometry.thickness must be a number');
  return { ok: true, value: 0 };
}

function validateFillGeometry(input: Record<string, unknown>): ValidationResult<BraceGeometry> {
  const rawPoints = input.points;
  if (!Array.isArray(rawPoints)) return fail('geometry.points must be an array');
  if (rawPoints.length < 3) return fail('geometry.points must contain at least 3 points');
  if (rawPoints.length > MAX_PLANE_POINTS) {
    return fail(`geometry.points must contain at most ${MAX_PLANE_POINTS} points`);
  }
  const points: Vec3[] = [];
  for (let i = 0; i < rawPoints.length; i++) {
    const parsed = validateVec3(rawPoints[i], `geometry.points[${i}]`);
    if (!parsed.ok) return parsed;
    points.push(parsed.value);
  }
  const thickness = parseFillThickness(input);
  if (!thickness.ok) return thickness;

  const result: BraceGeometry = {
    start: points[0],
    end: points[points.length - 1],
    points,
    closed: true,
    fill: true,
    thickness: thickness.value,
    rotation: 0,
  };
  if (input.normal !== undefined) {
    const normal = validateVec3(input.normal, 'geometry.normal');
    if (!normal.ok) return normal;
    const len = Math.hypot(normal.value.x, normal.value.y, normal.value.z);
    if (len < 1e-6) return fail('geometry.normal must not be a zero vector');
    result.normal = normal.value;
  }
  const arcs = validateBraceArcs(input.arcs, points.length, true);
  if (!arcs.ok) return arcs;
  if (arcs.value) result.arcs = arcs.value;
  if (input.extrude !== undefined) {
    if (isRecord(input.extrude)) {
      const extrude = validateVec3(input.extrude, 'geometry.extrude');
      if (!extrude.ok) return extrude;
      const length = Math.hypot(extrude.value.x, extrude.value.y, extrude.value.z);
      if (length > MAX_THICKNESS) return fail(`geometry.extrude length must be <= ${MAX_THICKNESS}`);
      if (length >= 1e-9) result.extrude = extrude.value;
    } else {
      const extrude = validateSignedSize(input.extrude, 'geometry.extrude', MAX_THICKNESS);
      if (!extrude.ok) return extrude;
      if (Math.abs(extrude.value) >= 1e-9) {
        const raw = result.normal ?? computePolygonNormal(points) ?? { x: 0, y: 1, z: 0 };
        const len = Math.hypot(raw.x, raw.y, raw.z);
        if (len >= 1e-9) {
          result.extrude = {
            x: (raw.x / len) * extrude.value,
            y: (raw.y / len) * extrude.value,
            z: (raw.z / len) * extrude.value,
          };
        }
      }
    }
  }
  const worldHoles = Array.isArray(input.holes) && (input.holes.length === 0 || Array.isArray(input.holes[0]));
  const capHoles = validateFillHoles(input.capHoles ?? (worldHoles ? input.holes : undefined));
  if (!capHoles.ok) return capHoles;
  if (capHoles.value) result.capHoles = capHoles.value;
  const uvHoles = validateFillSideHoles(
    input.sideHoles ?? (worldHoles ? undefined : input.holes),
    points.length
  );
  if (!uvHoles.ok) return uvHoles;
  if (uvHoles.value) result.holes = uvHoles.value;
  if ((result.capHoles?.length ?? 0) + (result.holes?.length ?? 0) > MAX_HOLES) {
    return fail(`geometry.holes must contain at most ${MAX_HOLES} holes`);
  }
  if (input.cloth !== undefined) {
    const cloth = validateClothBake(input.cloth);
    if (!cloth.ok) return cloth;
    result.cloth = cloth.value;
  }
  return attachBraceRevolve(result, input);
}

function validateFillSideHoles(
  input: unknown,
  edgeCount: number
): ValidationResult<BracePathHole[] | undefined> {
  if (input === undefined) return { ok: true, value: undefined };
  if (!Array.isArray(input)) return fail('geometry.sideHoles must be an array');
  if (input.length > MAX_HOLES) return fail(`geometry.sideHoles must contain at most ${MAX_HOLES} holes`);
  if (input.length === 0) return { ok: true, value: undefined };
  const holes: BracePathHole[] = [];
  for (let i = 0; i < input.length; i++) {
    const raw = input[i];
    if (!isRecord(raw)) return fail(`geometry.sideHoles[${i}] must be an object`);
    const segment = raw.segment;
    if (!Number.isInteger(segment) || (segment as number) < 0 || (segment as number) >= edgeCount) {
      return fail(
        `geometry.sideHoles[${i}].segment must be an integer 0–${Math.max(0, edgeCount - 1)}`
      );
    }
    const uv = validateHoleUv(raw.uv, `geometry.sideHoles[${i}].uv`);
    if (!uv.ok) return uv;
    holes.push({ segment: segment as number, uv: uv.value });
  }
  return { ok: true, value: holes };
}

function attachBraceExtrude(
  result: BraceGeometry,
  input: Record<string, unknown>
): ValidationResult<BraceGeometry> {
  if (input.extrude !== undefined) {
    const extrude = validateVec3(input.extrude, 'geometry.extrude');
    if (!extrude.ok) return extrude;
    const length = Math.hypot(extrude.value.x, extrude.value.y, extrude.value.z);
    if (length > MAX_THICKNESS) return fail(`geometry.extrude length must be <= ${MAX_THICKNESS}`);
    if (length >= 1e-9) result.extrude = extrude.value;
  }
  const holes = validatePathHoles(input.holes, result);
  if (!holes.ok) return holes;
  if (holes.value) result.holes = holes.value;
  if (input.cloth !== undefined) {
    const cloth = validateClothBake(input.cloth);
    if (!cloth.ok) return cloth;
    result.cloth = cloth.value;
  }
  return attachBraceRevolve(result, input);
}

function logicalSegmentCount(geometry: BraceGeometry): number {
  const { points, closed } = pathPointCount(geometry);
  return closed || geometry.fill === true ? points : Math.max(0, points - 1);
}

function attachBraceRevolve(
  result: BraceGeometry,
  input: Record<string, unknown>
): ValidationResult<BraceGeometry> {
  if (input.revolve === undefined) return { ok: true, value: result };
  if (!isRecord(input.revolve)) return fail('geometry.revolve must be an object');
  const axis = input.revolve.axis;
  if (!Number.isInteger(axis)) return fail('geometry.revolve.axis must be an integer');
  const count = logicalSegmentCount(result);
  if ((axis as number) < 0 || (axis as number) >= count) {
    return fail(`geometry.revolve.axis must be an integer 0–${Math.max(0, count - 1)}`);
  }
  if (count < 2) return fail('geometry.revolve needs at least 2 segments');
  if (result.arcs?.some((arc) => arc.segment === axis)) {
    return fail('geometry.revolve.axis must be a straight segment');
  }
  const extrude = result.extrude;
  if (extrude && Math.hypot(extrude.x, extrude.y, extrude.z) >= 1e-9) {
    return fail('geometry.revolve cannot be combined with extrude');
  }
  if ((result.capHoles?.length ?? 0) > 0 || (result.holes?.length ?? 0) > 0) {
    return fail('geometry.revolve cannot be combined with holes');
  }
  if (result.cloth) return fail('geometry.revolve cannot be combined with cloth');
  const points = result.points ?? [result.start, result.end];
  if (points.length >= 3 && !isPlanar(points)) {
    return fail('geometry.revolve requires a planar brace');
  }
  result.revolve = { axis: axis as number };
  return { ok: true, value: result };
}

function validateFillHoles(input: unknown): ValidationResult<Vec3[][] | undefined> {
  if (input === undefined) return { ok: true, value: undefined };
  if (!Array.isArray(input)) return fail('geometry.holes must be an array');
  if (input.length > MAX_HOLES) return fail(`geometry.holes must contain at most ${MAX_HOLES} holes`);
  if (input.length === 0) return { ok: true, value: undefined };
  const holes: Vec3[][] = [];
  for (let i = 0; i < input.length; i++) {
    const ring = validateHoleRing(input[i], `geometry.holes[${i}]`);
    if (!ring.ok) return ring;
    holes.push(ring.value);
  }
  return { ok: true, value: holes };
}

function validatePathHoles(
  input: unknown,
  geometry: BraceGeometry
): ValidationResult<BracePathHole[] | undefined> {
  if (input === undefined) return { ok: true, value: undefined };
  if (!Array.isArray(input)) return fail('geometry.holes must be an array');
  if (input.length > MAX_HOLES) return fail(`geometry.holes must contain at most ${MAX_HOLES} holes`);
  if (input.length === 0) return { ok: true, value: undefined };
  const { points, closed } = pathPointCount(geometry);
  const segmentCount = closed ? points : Math.max(0, points - 1);
  const holes: BracePathHole[] = [];
  for (let i = 0; i < input.length; i++) {
    const raw = input[i];
    if (!isRecord(raw)) return fail(`geometry.holes[${i}] must be an object`);
    const segment = raw.segment;
    if (!Number.isInteger(segment) || (segment as number) < 0 || (segment as number) >= segmentCount) {
      return fail(
        `geometry.holes[${i}].segment must be an integer 0–${Math.max(0, segmentCount - 1)}`
      );
    }
    const uv = validateHoleUv(raw.uv, `geometry.holes[${i}].uv`);
    if (!uv.ok) return uv;
    holes.push({ segment: segment as number, uv: uv.value });
  }
  return { ok: true, value: holes };
}

function pathPointCount(geometry: BraceGeometry): { points: number; closed: boolean } {
  if (geometry.points && geometry.points.length >= 2) {
    return { points: geometry.points.length, closed: geometry.closed === true };
  }
  return { points: 2, closed: false };
}

function validateHoleRing(input: unknown, path: string): ValidationResult<Vec3[]> {
  if (!Array.isArray(input)) return fail(`${path} must be an array`);
  if (input.length < 3) return fail(`${path} must contain at least 3 points`);
  if (input.length > MAX_PLANE_POINTS) {
    return fail(`${path} must contain at most ${MAX_PLANE_POINTS} points`);
  }
  const points: Vec3[] = [];
  for (let i = 0; i < input.length; i++) {
    const parsed = validateVec3(input[i], `${path}[${i}]`);
    if (!parsed.ok) return parsed;
    points.push(parsed.value);
  }
  for (let i = 0; i < points.length; i++) {
    if (vecEqual(points[i], points[(i + 1) % points.length])) {
      return fail(`${path} must not contain identical adjacent vertices`);
    }
  }
  return { ok: true, value: points };
}

function validateHoleUv(
  input: unknown,
  path: string
): ValidationResult<{ u: number; v: number }[]> {
  if (!Array.isArray(input)) return fail(`${path} must be an array`);
  if (input.length < 3) return fail(`${path} must contain at least 3 points`);
  if (input.length > MAX_PLANE_POINTS) {
    return fail(`${path} must contain at most ${MAX_PLANE_POINTS} points`);
  }
  const uv: { u: number; v: number }[] = [];
  for (let i = 0; i < input.length; i++) {
    const raw = input[i];
    if (!isRecord(raw)) return fail(`${path}[${i}] must be an object`);
    if (!isFiniteNumber(raw.u) || !isFiniteNumber(raw.v)) {
      return fail(`${path}[${i}].u and .v must be finite numbers`);
    }
    uv.push({ u: raw.u as number, v: raw.v as number });
  }
  return { ok: true, value: uv };
}

function validateClothBake(input: unknown): ValidationResult<ClothBake> {
  if (!isRecord(input)) return fail('geometry.cloth must be an object');
  const settings = validateClothSettings(input.settings);
  if (!settings.ok) return settings;
  const mesh = validateClothMesh(input.mesh);
  if (!mesh.ok) return mesh;
  return { ok: true, value: { settings: settings.value, mesh: mesh.value } };
}

const CLOTH_SHAPES: ClothShape[] = ['volume', 'sheet'];
const CLOTH_PINS: ClothPin[] = ['none', 'top', 'bottom', 'left', 'right', 'ends'];

function validateClothSettings(input: unknown): ValidationResult<ClothSimSettings> {
  if (input === undefined) {
    return {
      ok: true,
      value: {
        resolution: 8,
        steps: 160,
        inflate: 0.35,
        gravity: 0.8,
        stiffness: 0.85,
        shape: 'volume',
        pin: 'none',
        gather: 0,
      },
    };
  }
  if (!isRecord(input)) return fail('geometry.cloth.settings must be an object');
  return {
    ok: true,
    value: {
      resolution: clampRange(input.resolution, 8, 4, 100),
      steps: clampRange(input.steps, 160, 20, 400, true),
      inflate: clampRange(input.inflate, 0.35, 0, 8),
      gravity: clampRange(input.gravity, 0.8, 0, 4),
      stiffness: clampRange(input.stiffness, 0.85, 0, 1),
      shape: parseEnum(input.shape, CLOTH_SHAPES, 'volume'),
      pin: parseEnum(input.pin, CLOTH_PINS, 'none'),
      gather: clampRange(input.gather, 0, 0, 1),
    },
  };
}

function parseEnum<T extends string>(value: unknown, allowed: T[], fallback: T): T {
  return typeof value === 'string' && (allowed as string[]).includes(value) ? (value as T) : fallback;
}

function clampRange(
  value: unknown,
  fallback: number,
  min: number,
  max: number,
  integer = false
): number {
  const n = isFiniteNumber(value) ? (integer ? Math.round(value) : value) : fallback;
  return Math.min(max, Math.max(min, n));
}

function validateClothMesh(input: unknown): ValidationResult<ClothMesh> {
  if (!isRecord(input)) return fail('geometry.cloth.mesh must be an object');
  const positions = validateNumberArray(input.positions, 'geometry.cloth.mesh.positions', 9, 32768 * 3);
  if (!positions.ok) return positions;
  if (positions.value.length % 3 !== 0) {
    return fail('geometry.cloth.mesh.positions length must be a multiple of 3');
  }
  const vertexCount = positions.value.length / 3;
  const normals = validateNumberArray(input.normals, 'geometry.cloth.mesh.normals', positions.value.length, positions.value.length);
  if (!normals.ok) return normals;
  const uvs = validateNumberArray(input.uvs, 'geometry.cloth.mesh.uvs', vertexCount * 2, vertexCount * 2);
  if (!uvs.ok) return uvs;
  const indices = validateIndexArray(input.indices, vertexCount);
  if (!indices.ok) return indices;
  return {
    ok: true,
    value: {
      positions: positions.value,
      normals: normals.value,
      uvs: uvs.value,
      indices: indices.value,
    },
  };
}

function validateNumberArray(
  input: unknown,
  path: string,
  minLength: number,
  maxLength: number
): ValidationResult<number[]> {
  if (!Array.isArray(input)) return fail(`${path} must be an array`);
  if (input.length < minLength || input.length > maxLength) {
    return fail(`${path} must contain ${minLength}–${maxLength} numbers`);
  }
  const values: number[] = [];
  for (let i = 0; i < input.length; i++) {
    if (!isFiniteNumber(input[i])) return fail(`${path}[${i}] must be a finite number`);
    values.push(input[i] as number);
  }
  return { ok: true, value: values };
}

function validateIndexArray(input: unknown, vertexCount: number): ValidationResult<number[]> {
  if (!Array.isArray(input)) return fail('geometry.cloth.mesh.indices must be an array');
  if (input.length < 3 || input.length > 196608) {
    return fail('geometry.cloth.mesh.indices must contain 3–196608 numbers');
  }
  if (input.length % 3 !== 0) return fail('geometry.cloth.mesh.indices length must be a multiple of 3');
  const values: number[] = [];
  for (let i = 0; i < input.length; i++) {
    const value = input[i];
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value >= vertexCount) {
      return fail(`geometry.cloth.mesh.indices[${i}] must be an integer 0–${vertexCount - 1}`);
    }
    values.push(value);
  }
  return { ok: true, value: values };
}

/**
 * 0〜max の有限な値。省略時・負値は `fallback`、max 超は max に丸める。
 * 点光源コンポーネントの intensity / color / range 用。
 */
function validateBounded(v: unknown, fallback: number, max: number): number {
  if (!isFiniteNumber(v)) return fallback;
  if (v < 0) return fallback;
  return Math.min(v, max);
}

function validateComponents(input: unknown): ValidationResult<EntityComponents | undefined> {
  if (input === undefined || input === null) return { ok: true, value: undefined };
  if (!isRecord(input)) return fail('entity.components must be an object');

  const lights = validateLights(input.lights);
  if (!lights.ok) return lights;

  const value: EntityComponents = {};
  if (lights.value) value.lights = lights.value;
  return { ok: true, value: Object.keys(value).length > 0 ? value : undefined };
}

function validateLights(input: unknown): ValidationResult<PointLightComponent[] | undefined> {
  if (input === undefined || input === null) return { ok: true, value: undefined };
  if (!Array.isArray(input)) return fail('entity.components.lights must be an array');
  if (input.length > MAX_ENTITY_LIGHTS) {
    return fail(`entity.components.lights must contain at most ${MAX_ENTITY_LIGHTS} lights`);
  }

  const lights: PointLightComponent[] = [];
  for (let i = 0; i < input.length; i++) {
    const raw = input[i];
    const path = `entity.components.lights[${i}]`;
    if (!isRecord(raw)) return fail(`${path} must be an object`);
    if (raw.kind !== 'point') return fail(`${path}.kind must be point`);
    const light: PointLightComponent = {
      kind: 'point',
      intensity: validateBounded(raw.intensity, DEFAULT_LIGHT_INTENSITY, MAX_LIGHT_INTENSITY),
      color: validateBounded(raw.color, DEFAULT_LIGHT_COLOR, 0xffffff),
      range: validateBounded(raw.range, DEFAULT_LIGHT_RANGE, MAX_LIGHT_RANGE),
    };
    if (raw.offset !== undefined) {
      const offset = validateVec3(raw.offset, `${path}.offset`);
      if (!offset.ok) return offset;
      light.offset = offset.value;
    }
    lights.push(light);
  }
  return { ok: true, value: lights.length > 0 ? lights : undefined };
}

/**
 * 未検証の入力（WebSocket 越しの JSON）を Entity として検証し、
 * 既知のフィールドだけを持つ正規化済みオブジェクトを返す。
 */
export function validateEntity(input: unknown): ValidationResult<Entity> {
  if (!isRecord(input)) return fail('entity must be an object');

  const id = input.id;
  if (typeof id !== 'string' || id.length === 0 || id.length > 128) {
    return fail('entity.id must be a non-empty string (<= 128 chars)');
  }

  const type = input.type;
  if (typeof type !== 'string' || !ENTITY_INPUT_TYPES.includes(type as EntityInputType)) {
    return fail(`entity.type must be one of ${ENTITY_INPUT_TYPES.join(' | ')}`);
  }

  let material = 'default';
  if (input.material !== undefined && input.material !== null) {
    if (typeof input.material !== 'string' || input.material.length > MAX_MATERIAL_LENGTH) {
      return fail(`entity.material must be a string (<= ${MAX_MATERIAL_LENGTH} chars)`);
    }
    material = input.material;
  }

  const geometry = input.geometry;
  if (!isRecord(geometry)) return fail('entity.geometry must be an object');

  const components = validateComponents(input.components);
  if (!components.ok) return components;
  const base = {
    id,
    material,
    ...(components.value ? { components: components.value } : {}),
  };

  switch (type as EntityInputType) {
    case 'brace': {
      if (geometry.mode !== undefined && geometry.mode !== 'path' && geometry.mode !== 'fill') {
        return fail('geometry.mode must be path or fill');
      }
      if (isFillBraceInput(geometry)) {
        const fill = validateFillGeometry(geometry);
        if (!fill.ok) return fill;
        return { ok: true, value: { ...base, type: 'brace', geometry: fill.value } };
      }
      const brace = validateBraceGeometry(geometry);
      if (!brace.ok) return brace;
      return { ok: true, value: { ...base, type: 'brace', geometry: brace.value } };
    }

    case 'plane': {
      const fill = validateFillGeometry(geometry);
      if (!fill.ok) return fill;
      return { ok: true, value: { ...base, type: 'brace', geometry: fill.value } };
    }

    case 'stair': {
      const stair = validateStairGeometry(geometry);
      if (!stair.ok) return stair;
      return { ok: true, value: { ...base, type: 'stair', geometry: stair.value } };
    }

    case 'object': {
      const definitionId = input.definitionId;
      if (
        typeof definitionId !== 'string' ||
        definitionId.length === 0 ||
        definitionId.length > MAX_OBJECT_DEFINITION_ID
      ) {
        return fail(`entity.definitionId must be a non-empty string (<= ${MAX_OBJECT_DEFINITION_ID} chars)`);
      }
      const object = validateObjectGeometry(geometry);
      if (!object.ok) return object;
      return { ok: true, value: { ...base, type: 'object', definitionId, geometry: object.value } };
    }
  }
}

function validateObjectGeometry(input: Record<string, unknown>): ValidationResult<ObjectGeometry> {
  const origin = validateVec3(input.origin, 'geometry.origin');
  if (!origin.ok) return origin;
  if (!isFiniteNumber(input.yaw) || !isObjectYaw(input.yaw)) {
    return fail('geometry.yaw must be a finite number');
  }
  const yaw = input.yaw as ObjectYaw;
  if (input.mount === undefined || input.mount === null) {
    return { ok: true, value: { origin: origin.value, yaw } };
  }
  if (typeof input.mount !== 'string' || !isObjectMountAxis(input.mount)) {
    return fail('geometry.mount must be +x, -x, +y, -y, +z, or -z');
  }
  return { ok: true, value: { origin: origin.value, yaw, mount: input.mount as ObjectMountAxis } };
}

function validateStairGeometry(input: Record<string, unknown>): ValidationResult<StairGeometry> {
  const start = validateVec3(input.start, 'geometry.start');
  if (!start.ok) return start;
  const end = validateVec3(input.end, 'geometry.end');
  if (!end.ok) return end;
  if (vecEqual(start.value, end.value)) {
    return fail('geometry.start and geometry.end must not be identical');
  }
  const horizontal = Math.hypot(end.value.x - start.value.x, end.value.z - start.value.z);
  if (horizontal < 1e-9) {
    return fail('geometry.start and geometry.end must have a horizontal span');
  }
  if (input.width === undefined) {
    return { ok: true, value: { start: start.value, end: end.value } };
  }
  const width = validatePositiveSize(input.width, 'geometry.width', MAX_THICKNESS);
  if (!width.ok) return width;
  return { ok: true, value: { start: start.value, end: end.value, width: width.value } };
}

/**
 * 点群が（許容誤差内で）同一平面上にあるかを判定する。§4.3 のとおり
 * クライアント側のバリデーション用で、サーバーからは呼ばれない。
 */
export function isPlanar(points: Vec3[], epsilon = PLANARITY_EPSILON): boolean {
  if (points.length < 3) return false;
  const normal = computePlanarNormal(points);
  if (!normal) return false;
  const origin = points[0];
  for (const p of points) {
    const d =
      (p.x - origin.x) * normal.x + (p.y - origin.y) * normal.y + (p.z - origin.z) * normal.z;
    if (Math.abs(d) > epsilon) return false;
  }
  return true;
}

/**
 * 同一平面点群の法線。Newell が符号付き面積の打ち消しで 0 になる
 * S 字折れ線などは、最大面積の三角形から取る。
 */
export function computePlanarNormal(points: Vec3[]): Vec3 | null {
  return computePolygonNormal(points) ?? planeNormalFromTriangles(points);
}

function planeNormalFromTriangles(points: Vec3[]): Vec3 | null {
  let bestNx = 0;
  let bestNy = 0;
  let bestNz = 0;
  let bestLen2 = 0;
  const n = points.length;
  for (let i = 0; i < n - 2; i++) {
    const a = points[i];
    const b = points[i + 1];
    const ux = b.x - a.x;
    const uy = b.y - a.y;
    const uz = b.z - a.z;
    for (let j = i + 2; j < n; j++) {
      const c = points[j];
      const nx = uy * (c.z - a.z) - uz * (c.y - a.y);
      const ny = uz * (c.x - a.x) - ux * (c.z - a.z);
      const nz = ux * (c.y - a.y) - uy * (c.x - a.x);
      const len2 = nx * nx + ny * ny + nz * nz;
      if (len2 > bestLen2) {
        bestNx = nx;
        bestNy = ny;
        bestNz = nz;
        bestLen2 = len2;
      }
    }
  }
  if (bestLen2 < 1e-18) return null;
  const len = Math.sqrt(bestLen2);
  return { x: bestNx / len, y: bestNy / len, z: bestNz / len };
}

/**
 * 点群から面法線を求める（Newell 法）。縮退している場合は null。
 * §4.3「normal 省略時は点群から自動計算」で使用する。
 */
export function computePolygonNormal(points: Vec3[]): Vec3 | null {
  if (points.length < 3) return null;
  let nx = 0;
  let ny = 0;
  let nz = 0;
  for (let i = 0; i < points.length; i++) {
    const cur = points[i];
    const next = points[(i + 1) % points.length];
    nx += (cur.y - next.y) * (cur.z + next.z);
    ny += (cur.z - next.z) * (cur.x + next.x);
    nz += (cur.x - next.x) * (cur.y + next.y);
  }
  const len = Math.hypot(nx, ny, nz);
  if (len < 1e-9) return null;
  return { x: nx / len, y: ny / len, z: nz / len };
}
